import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ChatbotSession,
  continueChatbot,
  getChatbotById,
  getChatbotMessages,
  getChatbotsByBeneficiary,
  startChatbotSessionTimer,
  submitChatbot,
  updateChatbotSessionDuration,
} from '../services/ai.service';
import config from '../services/config';
import useMe from './useMe';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
}

type ProblemType = 'emotional-support' | 'problem-solving' | 'situation-clarity';
type ViewMode = 'main' | 'history' | 'conversation';

const PREDEFINED_QUERIES_MAP: Record<string, ProblemType> = {
  'Je rencontre un problème\nJe veux trouver une solution': 'problem-solving',
  "Je ressens des émotions fortes\nJ'ai besoin d'aide pour mieux les gérer": 'emotional-support',
  "J'ai besoin qu'on m'aide\nJe veux comprendre une situation compliquée": 'situation-clarity',
};

const STORAGE_KEY = 'ia_chat_state';

const loadPersistedState = () => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        messages:
          parsed.messages?.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp),
          })) || [],
        currentChatbotId: parsed.currentChatbotId || null,
        viewMode: parsed.viewMode || 'main',
      };
    }
  } catch (error) {
    // Ignore errors
  }
  return { messages: [], currentChatbotId: null, viewMode: 'main' as ViewMode };
};

interface UseIAChatProps {
  onCloseCallback?: (chatbotId: number) => void;
}

const useIAChat = (props?: UseIAChatProps) => {
  const { onCloseCallback } = props || {};
  const persistedState = loadPersistedState();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>(persistedState.messages);
  const [inputValue, setInputValue] = useState('');
  const [currentChatbotId, setCurrentChatbotId] = useState<number | null>(
    persistedState.currentChatbotId
  );
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(persistedState.viewMode);
  const [chatbotHistory, setChatbotHistory] = useState<ChatbotSession[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Timer state
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);
  const [baseSessionDuration, setBaseSessionDuration] = useState<number>(0); // Duration from DB (previous cumulative)
  const [currentElapsedTime, setCurrentElapsedTime] = useState<number>(0); // Time of current session

  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { me } = useMe();

  // Persist chat state
  useEffect(() => {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        messages,
        currentChatbotId,
        viewMode,
      })
    );
  }, [messages, currentChatbotId, viewMode]);

  // Start timer
  const startTimer = useCallback(async () => {
    if (!currentChatbotId || sessionStartTime) return;

    const now = new Date();
    setSessionStartTime(now);
    setCurrentElapsedTime(0); // Reset current session counter

    try {
      await startChatbotSessionTimer(currentChatbotId);
    } catch (error) {
      console.error('Failed to start session timer:', error);
    }
  }, [currentChatbotId, sessionStartTime]);

  // Stop timer and save duration
  const stopTimer = useCallback(async () => {
    if (!currentChatbotId) return;

    // If timer was started, calculate duration; otherwise save 0
    let sessionDuration = 0;
    if (sessionStartTime) {
      const now = new Date();
      sessionDuration = Math.floor((now.getTime() - sessionStartTime.getTime()) / 1000);
    }

    // Only save if duration > 0 (timer was actually running)
    if (sessionDuration > 0) {
      try {
        // Send only the duration of the current session (not the total)
        const result = await updateChatbotSessionDuration(currentChatbotId, sessionDuration);

        // Update base for next opening
        setBaseSessionDuration(result.totalDuration);
      } catch (error) {
        console.error('Failed to save session duration:', error);
      }
    }

    // Reset timer state
    setCurrentElapsedTime(0);
    setSessionStartTime(null);
  }, [currentChatbotId, sessionStartTime]);

  // Timer interval to update current elapsed time
  useEffect(() => {
    if (isDrawerOpen && sessionStartTime && currentChatbotId) {
      timerIntervalRef.current = setInterval(() => {
        const now = new Date();
        const elapsed = Math.floor((now.getTime() - sessionStartTime.getTime()) / 1000);
        setCurrentElapsedTime(elapsed);
      }, 1000);
    } else if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [isDrawerOpen, sessionStartTime, currentChatbotId]);

  // Start timer when drawer opens with a chatbot
  useEffect(() => {
    if (isDrawerOpen && currentChatbotId && !sessionStartTime) {
      startTimer();
    }
  }, [isDrawerOpen, currentChatbotId, sessionStartTime, startTimer]);

  // Save on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentChatbotId && sessionStartTime) {
        const now = new Date();
        const sessionDuration = Math.floor((now.getTime() - sessionStartTime.getTime()) / 1000);

        // Use fetch with keepalive for reliable async request on page unload
        const token = localStorage.getItem('token') || '';
        const apiUrl = `${config.ai_url}/api/chatbot/${currentChatbotId}/session/duration`;
        const data = JSON.stringify({ durationInSeconds: sessionDuration });

        fetch(apiUrl, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: data,
          keepalive: true,
        }).catch(() => {
          // Ignore errors on unload
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentChatbotId, sessionStartTime]);

  const openDrawer = () => {
    setIsDrawerOpen(true);
  };

  const minimizeDrawer = async () => {
    await stopTimer(); // Save time on minimize
    setIsDrawerOpen(false);
  };

  const closeDrawer = async () => {
    const chatbotToReview = currentChatbotId;
    await stopTimer(); // Save time on close
    setIsDrawerOpen(false);
    setViewMode('main');
    setMessages([]);
    setCurrentChatbotId(null);
    setBaseSessionDuration(0); // Reset
    sessionStorage.removeItem(STORAGE_KEY);

    // Trigger feedback modal after 2 seconds if callback provided and chatbot exists
    if (onCloseCallback && chatbotToReview) {
      setTimeout(() => {
        onCloseCallback(chatbotToReview);
      }, 2000);
    }
  };

  const loadHistory = useCallback(async () => {
    if (!me?.id || isLoadingHistory) return;

    setIsLoadingHistory(true);
    try {
      const history = await getChatbotsByBeneficiary(me.id.toString());
      if(history) {
        setChatbotHistory(history);
      }
    } catch (error) {
      setChatbotHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [me, isLoadingHistory]);

  const showHistory = useCallback(() => {
    setViewMode('history');
    loadHistory();
  }, [loadHistory]);

  const backToMain = useCallback(() => {
    setViewMode('main');
    setMessages([]);
    setCurrentChatbotId(null);
    setBaseSessionDuration(0); // Reset
  }, []);

  const loadConversation = useCallback(async (chatbotId: number) => {
    setIsLoading(true);
    setViewMode('conversation');
    setCurrentChatbotId(chatbotId);

    try {
      // Get messages
      const apiMessages = await getChatbotMessages(chatbotId);
      const formattedMessages: Message[] = apiMessages.map(msg => ({
        id: Date.now().toString() + Math.random(),
        text: msg.content,
        sender: msg.role,
        timestamp: new Date(msg.createdAt || new Date()),
      }));
      setMessages(formattedMessages);

      // Get existing session duration from DB
      const chatbotDetails = await getChatbotById(chatbotId);
      setBaseSessionDuration(chatbotDetails.totalSessionDuration || 0);
    } catch (error) {
      setMessages([]);
      setBaseSessionDuration(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isDrawerOpen && me?.id && !isLoadingHistory) {
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, me?.id]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isLoading || !me?.id) return;

      const userMessage: Message = {
        id: Date.now().toString(),
        text: text.trim(),
        sender: 'user',
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, userMessage]);
      setInputValue('');
      setIsLoading(true);

      try {
        if (currentChatbotId) {
          const { response } = await continueChatbot(currentChatbotId, text.trim());

          const assistantMessage: Message = {
            id: (Date.now() + 1).toString(),
            text: response,
            sender: 'assistant',
            timestamp: new Date(),
          };

          setMessages(prev => [...prev, assistantMessage]);
        }
      } catch (error) {
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: "Désolé, une erreur s'est produite. Veuillez réessayer.",
          sender: 'assistant',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, errorMessage]);
      } finally {
        setIsLoading(false);
      }
    },
    [currentChatbotId, isLoading, me]
  );

  const selectPredefinedQuery = useCallback(
    async (query: string) => {
      if (isLoading || !me?.id) return;

      const problemType = PREDEFINED_QUERIES_MAP[query];
      if (!problemType) return;

      const userMessage: Message = {
        id: Date.now().toString(),
        text: query,
        sender: 'user',
        timestamp: new Date(),
      };

      setMessages([userMessage]);
      setIsLoading(true);
      setBaseSessionDuration(0); // Reset for new conversation

      try {
        const { chatbotId, response } = await submitChatbot(me.id.toString(), problemType);

        setCurrentChatbotId(chatbotId);

        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: response,
          sender: 'assistant',
          timestamp: new Date(),
        };

        setMessages(prev => [...prev, assistantMessage]);
      } catch (error) {
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: "Désolé, une erreur s'est produite. Veuillez réessayer.",
          sender: 'assistant',
          timestamp: new Date(),
        };
        setMessages([userMessage, errorMessage]);
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, me]
  );

  return {
    isDrawerOpen,
    openDrawer,
    minimizeDrawer,
    closeDrawer,
    messages,
    inputValue,
    setInputValue,
    sendMessage,
    selectPredefinedQuery,
    isLoading,
    viewMode,
    chatbotHistory,
    isLoadingHistory,
    showHistory,
    backToMain,
    loadConversation,
  };
};

export default useIAChat;
